import { startCase } from 'es-toolkit';
import type { EntityTypeConfiguration, MenuConfig } from '@/services/ConfigService';
import { filterNavigableEntityTypes, isEntityTypePageDisabled } from '@/utils/entityTypeConfig';

export type MenuLeaf = {
  kind: 'leaf';
  key: string;
  /**
   * The underlying entity type this leaf navigates to, when applicable.
   * Distinct from `key` for category-leaf destinations whose menu key (e.g.
   * 'asset-management') differs from the entity type it maps to (e.g.
   * 'asset') — callers that need to cross-reference an entity type against
   * the menu (e.g. search pages) must key off `entityType`, not `key` (#1011).
   */
  entityType?: string;
  label: string;
  iconName?: string;
  route: `/${string}`;
  order?: number[];
  adminOnly?: boolean;
  viewerHidden?: boolean;
  comptrollerHidden?: boolean;
};

export type MenuGroup = {
  kind: 'group';
  key: string; // group key
  label: string; // Display name from central UI config
  iconName?: string; // group icon from central UI config
  comptrollerHidden?: boolean;
  children: MenuLeaf[];
};

export type MenuItem = MenuLeaf | MenuGroup;

/** Role flags used to resolve effective navigation-item visibility (#1011). */
export interface MenuVisibilityFlags {
  isAdmin: boolean;
  isViewer: boolean;
  isComptroller: boolean;
  isHr: boolean;
}

/**
 * Applies the same role-based visibility rules the sidebar `Menu` uses
 * (`adminOnly`, `viewerHidden`, `comptrollerHidden`) to a list of menu items.
 *
 * This is the single source of truth for which navigation destinations are
 * visible to the current user. It must be used everywhere a destination list
 * derived from `buildMenu()` is rendered or made discoverable — including the
 * sidebar Menu and both global search surfaces (autocomplete dropdown and the
 * Global Search page) — so restricted routes cannot be discovered through
 * search when they are hidden from navigation (#1011).
 */
export function filterVisibleMenuItems(
  items: MenuItem[],
  { isAdmin, isViewer, isComptroller, isHr }: MenuVisibilityFlags,
): MenuItem[] {
  return items
    .map((item) => {
      if (item.kind === 'group') {
        if (item.comptrollerHidden && (isComptroller || isHr)) return null;
        const children = (isAdmin ? item.children : item.children.filter((leaf) => !leaf.adminOnly))
          .filter((leaf) => !(leaf.viewerHidden && isViewer))
          .filter((leaf) => !(leaf.comptrollerHidden && (isComptroller || isHr)));
        if (children.length === 0) return null;
        return { ...item, children };
      }
      if (item.adminOnly && !isAdmin) return null;
      if (item.viewerHidden && isViewer) return null;
      if (item.comptrollerHidden && (isComptroller || isHr)) return null;
      return item;
    })
    .filter((item): item is MenuItem => item !== null);
}

/** Flattens (possibly grouped) menu items into a set of their leaf `key`s. */
export function collectMenuLeafKeys(items: MenuItem[]): Set<string> {
  const keys = new Set<string>();
  for (const item of items) {
    if (item.kind === 'leaf') {
      keys.add(item.key);
    } else {
      item.children.forEach((leaf) => keys.add(leaf.key));
    }
  }
  return keys;
}

/**
 * Flattens (possibly grouped) menu items into a set of the entity types they
 * navigate to. Unlike `collectMenuLeafKeys`, this uses each leaf's
 * `entityType` (falling back to `key` when `entityType` is not set) so
 * callers can reliably cross-reference an `EntityTypeConfiguration.entity_type`
 * against the menu, even for category-leaf destinations whose menu `key`
 * differs from the entity type (#1011).
 */
export function collectMenuLeafEntityTypes(items: MenuItem[]): Set<string> {
  const types = new Set<string>();
  for (const item of items) {
    if (item.kind === 'leaf') {
      types.add(item.entityType ?? item.key);
    } else {
      item.children.forEach((leaf) => types.add(leaf.entityType ?? leaf.key));
    }
  }
  return types;
}

export function buildMenu(types: EntityTypeConfiguration[], nav: MenuConfig | null): MenuItem[] {
  const navConfig = nav ?? { groups: {} };

  // Index types by key for quick lookups
  // Be defensive: ensure we have an array
  const typeList = filterNavigableEntityTypes(types);
  const typeByKey = new Map(typeList.map((t) => [t.entity_type, t] as const));

  // Track which entity types are considered grouped, either by
  // centralized nav config (entity_order) or by their own ui_configuration.group.
  const groupedKeys = new Set<string>();

  // Build group structures from central config
  const groupItems: MenuGroup[] = [];
  const groupOrder = navConfig.group_order ?? [];

  for (const gKey of groupOrder) {
    const gDef = navConfig.groups?.[gKey];
    if (!gDef) continue;
    // Collect children by configured order, falling back to alpha by display_name if missing
    const childrenKeys = gDef.entity_order ?? [];
    const children: MenuLeaf[] = [];
    for (const key of childrenKeys) {
      const t = typeByKey.get(key);
      if (!t) continue;
      groupedKeys.add(key);
      const leaf: MenuLeaf = {
        kind: 'leaf',
        key: t.entity_type,
        entityType: t.entity_type,
        label: t.display_name,
        // Prefer entity's icon, then fall back to group icon
        iconName: t.ui_configuration?.icon ?? gDef.icon,
        route: `/${t.entity_type}`,
      };
      children.push(leaf);
    }
    // Append any additional types that declare this group but weren't listed
    const extras = typeList
      .filter(
        (t) =>
          t.ui_configuration?.group === gKey &&
          !childrenKeys.includes(t.entity_type) &&
          !isEntityTypePageDisabled(t),
      )
      .map<MenuLeaf>((t) => {
        return {
          kind: 'leaf',
          key: t.entity_type,
          entityType: t.entity_type,
          label: t.display_name,
          iconName: t.ui_configuration?.icon ?? gDef.icon,
          route: `/${t.entity_type}`,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label));

    // Mark extras as grouped too
    extras.forEach((e) => groupedKeys.add(e.key));

    const categoryLeaves: MenuLeaf[] = (gDef.category_leaves ?? []).map((leaf) => {
      if (leaf.route) {
        if (leaf.entity_type) {
          groupedKeys.add(leaf.entity_type);
        }
        const route = (leaf.route.startsWith('/') ? leaf.route : `/${leaf.route}`) as `/${string}`;
        return {
          kind: 'leaf',
          key: leaf.key,
          entityType: leaf.entity_type || undefined,
          label: leaf.label,
          iconName: leaf.icon ?? gDef.icon,
          route,
          adminOnly: leaf.adminOnly,
          viewerHidden: leaf.viewerHidden,
          comptrollerHidden: leaf.comptrollerHidden,
        };
      } else if (leaf.entity_type) {
        groupedKeys.add(leaf.entity_type);
        const qs = new URLSearchParams(leaf.query ?? {});
        const path = qs.toString();
        const route = `/${leaf.entity_type}${path ? `?${path}` : ''}` as `/${string}`;
        return {
          kind: 'leaf',
          key: leaf.key,
          entityType: leaf.entity_type,
          label: leaf.label,
          iconName:
            leaf.icon ?? typeByKey.get(leaf.entity_type)?.ui_configuration?.icon ?? gDef.icon,
          route,
          adminOnly: leaf.adminOnly,
          viewerHidden: leaf.viewerHidden,
          comptrollerHidden: leaf.comptrollerHidden,
        };
      } else {
        // Route for items without entity_type - use explicit route or default to /${key}
        return {
          kind: 'leaf',
          key: leaf.key,
          entityType: leaf.entity_type || undefined,
          label: leaf.label,
          iconName: leaf.icon ?? gDef.icon,
          route: (leaf.route ?? `/${leaf.key}`) as `/${string}`,
          adminOnly: leaf.adminOnly,
          viewerHidden: leaf.viewerHidden,
          comptrollerHidden: leaf.comptrollerHidden,
        };
      }
    });

    const group: MenuGroup = {
      kind: 'group',
      key: gKey,
      label: gDef.display_name || startCase(gKey),
      iconName: gDef.icon,
      comptrollerHidden: gDef.comptrollerHidden,
      children: [...children, ...extras, ...categoryLeaves],
    };
    // Only include non-empty groups
    if (group.children.length > 0) {
      groupItems.push(group);
    }
  }

  // Top-level ungrouped leaves, ordered by ungrouped_entity_order then alpha
  const ungroupedOrder = navConfig.ungrouped_entity_order ?? [];
  const ungroupedSet = new Set(ungroupedOrder);

  const ungroupedLeavesAll = typeList
    // Exclude anything that is part of a group (by config or by its own declaration)
    .filter((t) => !groupedKeys.has(t.entity_type) && !t.ui_configuration?.group)
    .map<MenuLeaf>((t) => {
      return {
        kind: 'leaf',
        key: t.entity_type,
        entityType: t.entity_type,
        label: t.display_name,
        // Prefer entity's icon; no group fallback for ungrouped
        iconName: t.ui_configuration?.icon,
        route: `/${t.entity_type}`,
      };
    });

  const orderedUngrouped: MenuLeaf[] = [];
  for (const key of ungroupedOrder) {
    const leaf = ungroupedLeavesAll.find((l) => l.key === key);
    if (leaf) orderedUngrouped.push(leaf);
  }
  const leftoverUngrouped = ungroupedLeavesAll
    .filter((l) => !ungroupedSet.has(l.key))
    .sort((a, b) => a.label.localeCompare(b.label));

  // Compose final list. To preserve current UX, insert groups after 'customer_service' if present.
  const result: MenuItem[] = [];
  const insertionIdx = ungroupedOrder.indexOf('customer_service');
  if (insertionIdx >= 0) {
    // up to and including customer_service
    const before = orderedUngrouped.slice(0, insertionIdx + 1);
    const after = orderedUngrouped.slice(insertionIdx + 1);
    result.push(...before, ...groupItems, ...after, ...leftoverUngrouped);
  } else {
    result.push(...groupItems, ...orderedUngrouped, ...leftoverUngrouped);
  }

  // Add placeholder routes as ungrouped items
  const placeholderRoutes: MenuLeaf[] = (navConfig.placeholder_routes ?? []).map((placeholder) => ({
    kind: 'leaf',
    key: placeholder.key,
    label: placeholder.label,
    iconName: placeholder.icon,
    route: (placeholder.route ?? `/${placeholder.key}`) as `/${string}`,
  }));

  result.push(...placeholderRoutes);

  return result;
}
